import { useParams, Navigate } from 'react-router-dom';
import { LocalizedLink as Link } from '../components/LocalizedLink';
import { useEffect } from 'react';
import { PlayCircle, BrainCircuit, Settings, ArrowRight, ChevronLeft } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import SEO from '../components/SEO';
import { trackWalkthroughView } from '../services/analytics';
import SiteHeader from '../components/SiteHeader';
import SiteFooter from '../components/SiteFooter';
import './WalkthroughPage.css';
import { useTranslation } from "react-i18next";

interface WalkthroughData {
  slug: string;
  title: string;
  subtitle: string;
  description: string;
  icon: LucideIcon;
  videoUrl: string;
  highlights: string[];
}

const getWalkthroughsData = (t: any): Record<string, WalkthroughData> => ({
  'app': {
    slug: 'app',
    title: t('common.fullPlatformDemo'),
    subtitle: t('walkthrough.seeReviioInAction'),
    description: t('walkthrough.takeAComprehensiveTourOfThe'),
    icon: PlayCircle,
    videoUrl: '/recordings/app-demo.webp',
    highlights: [
      t('walkthrough.dashboardOverviewAndKeyMetrics'),
      t('walkthrough.reviewInsightsAndSentimentAnalysis'),
      t('walkthrough.automatedReviewCampaigns'),
      t('walkthrough.locationSpecificSettingsAndRules')
    ]
  },
  'insights': {
    slug: 'insights',
    title: t('common.aiReviewInsights'),
    subtitle: t('walkthrough.understandWhatYourCustomersTrulyThink'),
    description: t('walkthrough.watchHowOurAiAnalyzesThousands'),
    icon: BrainCircuit,
    videoUrl: '/recordings/insights-demo.webp',
    highlights: [
      t('walkthrough.interactiveMetricCardsAndFilters'),
      t('walkthrough.customActionableFeedbackTracking'),
      t('walkthrough.whatClientsLoveVsWhatClients'),
      t('walkthrough.employeeShoutoutsAndWeekOverWeek')
    ]
  },
  'settings': {
    slug: 'settings',
    title: t('walkthrough.locationSettingsCustomization'),
    subtitle: t('walkthrough.tailorReviioToYourBusinessNeeds'),
    description: t('walkthrough.exploreThePowerfulCustomizationOptionsAv'),
    icon: Settings,
    videoUrl: '/recordings/settings-demo.webp',
    highlights: [
      t('walkthrough.adjustableAiResponseTonesProfessionalVs'),
      t('walkthrough.starRatingSpecificAutomationRules'),
      t('walkthrough.businessContextGroundingWithDocumentUplo'),
      t('walkthrough.reviewWidgetAndGateConfiguration')
    ]
  }
});

export default function WalkthroughPage() {
  const { t } = useTranslation();
  const { slug } = useParams<{ slug: string }>();
  const walkthrough = slug ? getWalkthroughsData(t)[slug] : undefined;

  useEffect(() => {
    window.scrollTo(0, 0);
    if (slug) trackWalkthroughView(slug);
  }, [slug]);

  if (!walkthrough) {
    return <Navigate to="/" replace />;
  }

  const Icon = walkthrough.icon;

  return (
    <div className="walkthrough-page">
      <SEO 
        title={`${walkthrough.title} | Reviio Walkthrough`}
        description={walkthrough.description}
      />

      <SiteHeader variant="solid" />
      
      <div className="walkthrough-hero">
        <div className="walkthrough-container">
          <Link to="/" className="walkthrough-back-link">
            <ChevronLeft size={20} />{' ' + t('walkthrough.backToHome')}</Link>
          
          <div className="walkthrough-header">
            <div className="walkthrough-icon-wrapper">
              <Icon size={40} />
            </div>
            <h1>{walkthrough.title}</h1>
            <p className="walkthrough-subtitle">{walkthrough.subtitle}</p>
          </div>
        </div>
      </div>

      <div className="walkthrough-content">
        <div className="walkthrough-container">
          
          <div className="walkthrough-grid">
            <div className="walkthrough-info">
              <h2>{t('walkthrough.overview')}</h2>
              <p className="walkthrough-description">{walkthrough.description}</p>
              
              <div className="walkthrough-highlights">
                <h3>{t('walkthrough.keyHighlights')}</h3>
                <ul>
                  {walkthrough.highlights.map((highlight, idx) => (
                    <li key={idx}>
                      <ArrowRight size={18} className="highlight-icon" />
                      <span>{highlight}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="walkthrough-cta">
                <p>{t('walkthrough.readyToTryItYourself')}</p>
                <Link to="/signup" className="walkthrough-btn-primary">{' ' + t('walkthrough.startYourFreeTrial')}</Link>
              </div>
            </div>

            <div className="walkthrough-video-wrapper">
              <div className="walkthrough-browser-mockup">
                <div className="browser-header">
                  <div className="browser-dots">
                    <span className="dot red"></span>
                    <span className="dot yellow"></span>
                    <span className="dot green"></span>
                  </div>
                  <div className="browser-address-bar">{t('walkthrough.reviioApp')}{slug === 'app' ? '/dashboard' : slug === 'insights' ? '/dashboard/insights' : '/dashboard/locations'}</div>
                </div>
                <div className="browser-content">
                  <img src={walkthrough.videoUrl} alt={`${walkthrough.title} Recording`} className="walkthrough-video" />
                </div>
              </div>
            </div>
          </div>

        </div>
      </div>

      {/* Footer */}
      <SiteFooter />
    </div>
  );
}
